import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Loader2, PackageCheck, Truck } from "lucide-react";
import { formatIDR, pasangCopy } from "@kanata/shared";
import { Seo } from "@/components/Seo";
import { usePasangNotice } from "@/components/shop/PasangNotice";
import { RegionPicker, type RegionValue } from "@/components/shop/RegionPicker";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioCard, RadioGroup } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import {
  checkoutApi,
  discountRejected,
  needsRequote,
  packingKayuRejected,
  qrisUnavailable,
  type DiscountPreview,
  type ShippingRate,
  type VaBank,
} from "@/lib/api";
import { attributionForCheckout } from "@/lib/attribution";
import { gaIdsForCheckout } from "@/lib/google-tag";
import { pixelCookies } from "@/lib/meta-pixel";
import { mixpanelForCheckout } from "@/lib/mixpanel";
import {
  trackBeginCheckout,
  trackDiscountCodeRejected,
  trackOrderPlaced,
  trackOrderSubmitFailed,
  trackShippingQuoteFailed,
} from "@/lib/tracking";
import { useCart } from "@/lib/cart";
import { cartRows } from "@/lib/cart-upgrade";
import { checkoutTotals } from "@/lib/checkout-totals";

interface Form {
  name: string;
  email: string;
  phone: string;
  addressLine: string;
  district: string;
  city: string;
  province: string;
  postalCode: string;
  note: string;
}

const emptyForm: Form = {
  name: "",
  email: "",
  phone: "",
  addressLine: "",
  district: "",
  city: "",
  province: "",
  postalCode: "",
  note: "",
};

/**
 * Checkout. Guest by default — no account needed, and none offered mid-flow.
 *
 * Shipping is quoted live from Biteship as soon as a postal code is picked, so
 * the customer sees the real carriage cost before they agree to it rather than
 * a figure that changes after payment.
 */
export default function Checkout() {
  const { cart, loading, refresh } = useCart();
  const navigate = useNavigate();

  const [form, setForm] = useState<Form>(emptyForm);
  const [rates, setRates] = useState<ShippingRate[] | null>(null);
  const [rateError, setRateError] = useState<string | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [courier, setCourier] = useState<ShippingRate | null>(null);
  const [method, setMethod] = useState<"QRIS" | "VA">("QRIS");
  // Branded VA (DEL-439): when the server offers banks, the buyer picks one
  // and the VA number is shown on Kanata's own page. None offered: VA goes to
  // the payment partner's page as before, and there is no picker.
  const [vaBanks, setVaBanks] = useState<VaBank[]>([]);
  const [vaBank, setVaBank] = useState("");
  useEffect(() => {
    checkoutApi
      .paymentOptions()
      .then((o) => setVaBanks(o.vaBanks ?? []))
      .catch(() => setVaBanks([]));
  }, []);
  const needsBank = method === "VA" && vaBanks.length > 0;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeInput, setCodeInput] = useState("");
  const [discount, setDiscount] = useState<DiscountPreview | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [packing, setPacking] = useState(false);
  const pasangNotice = usePasangNotice();

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const items = cart?.items ?? [];
  const subtotal = cart?.subtotalIdr ?? 0;
  const shipping = courier?.price ?? 0;
  // The code comes off the merchandise after the cart's upgrades, never off
  // shipping. The server decides the real number at checkout; this is its
  // preview of the same arithmetic.
  const discountIdr = discount?.discountIdr ?? 0;
  // One crate per desk, at the price the cart was served with. Checkout reads
  // the price again and refuses if the add-on was switched off meanwhile. A
  // promotion that waives it crates every desk free, ticked or not.
  const promo = cart?.promo;
  const packingOffer = cart?.packingKayu;
  const totals = checkoutTotals(cart, { discountIdr, shippingIdr: shipping, packingTicked: packing });
  const packingIdr = totals.packingKayuIdr;
  const total = totals.totalIdr;
  // Pasang is a per-line choice made in the cart; the order-level flag is
  // simply whether anything in this order wants it.
  const wantsPasang = items.some((i) => i.pasang);

  const trackedItems = items.map((i) => ({ id: i.skuCode, name: i.productName, priceIdr: i.unitPriceIdr, qty: i.qty }));

  // Once per visit to the page, when there is something to check out. Mixpanel
  // only; the ad platforms hear of the checkout when the order is placed.
  const reportedCheckout = useRef(false);
  useEffect(() => {
    if (reportedCheckout.current || !cart?.items.length) return;
    reportedCheckout.current = true;
    trackBeginCheckout(
      cart.items.map((i) => ({ id: i.skuCode, name: i.productName, priceIdr: i.unitPriceIdr, qty: i.qty })),
    );
  }, [cart]);

  // Only the latest quote may land: picking a second postal code while the
  // first is still quoting must not show the first one's couriers.
  const quoteSeq = useRef(0);

  // `where` is the region being quoted, for the failure report: picking a
  // region sets the form and quotes in the same tick, before `form` updates.
  async function quote(postalCode: string, where: { province: string; city: string } = form) {
    if (!cart || !postalCode) return;
    const seq = ++quoteSeq.current;
    setQuoting(true);
    setRateError(null);
    setRates(null);
    setCourier(null);
    try {
      const { rates } = await checkoutApi.quote(cart.cartId, postalCode);
      if (seq !== quoteSeq.current) return;
      setRates(rates);
      if (rates.length === 0) {
        setRateError("Belum ada kurir yang melayani alamat ini.");
        trackShippingQuoteFailed("no_couriers", where.province, where.city);
      }
    } catch (e) {
      if (seq !== quoteSeq.current) return;
      setRateError(e instanceof Error ? e.message : "Gagal mengambil ongkir.");
      trackShippingQuoteFailed("request_failed", where.province, where.city);
    } finally {
      if (seq === quoteSeq.current) setQuoting(false);
    }
  }

  function setRegion(r: RegionValue) {
    const postalChanged = r.postalCode !== form.postalCode;
    setForm((f) => ({ ...f, ...r }));
    if (!postalChanged) return;
    if (r.postalCode) {
      void quote(r.postalCode, r);
    } else {
      // The address moved off the postal code the rates were quoted to.
      quoteSeq.current++;
      setQuoting(false);
      setRates(null);
      setCourier(null);
      setRateError(null);
    }
  }

  async function onApplyCode() {
    if (!cart || !codeInput.trim()) return;
    setApplying(true);
    setDiscountError(null);
    try {
      setDiscount(await checkoutApi.previewDiscount(cart.cartId, codeInput.trim()));
    } catch (e) {
      setDiscount(null);
      setDiscountError(e instanceof Error ? e.message : "Kode tidak bisa dipakai.");
      trackDiscountCodeRejected(codeInput.trim().toUpperCase());
    } finally {
      setApplying(false);
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!cart || !courier) return;
    // Pasang is booked by hand once a technician is free, and its fee is not
    // in this total, so say so before they pay — every time, even if they
    // hid the notice elsewhere.
    if (wantsPasang) {
      const productNames = [...new Set(items.filter((i) => i.pasang).map((i) => i.productName))];
      pasangNotice.show({ productNames, onContinue: () => void placeOrder(), always: true });
      return;
    }
    void placeOrder();
  }

  async function placeOrder() {
    if (!cart || !courier) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await checkoutApi.submit({
        cartId: cart.cartId,
        name: form.name,
        email: form.email,
        phone: form.phone,
        recipientName: form.name,
        addressLine: form.addressLine,
        district: form.district || undefined,
        city: form.city,
        province: form.province,
        postalCode: form.postalCode,
        // Just the quote id: the server charges what it quoted, and reads the
        // courier off the same record.
        shippingQuoteId: courier.quote_id,
        pasangIncluded: wantsPasang,
        packingKayu: packingIdr > 0 && !promo?.packingKayu,
        customerNote: form.note || undefined,
        paymentMethod: method,
        discountCode: discount?.code,
        vaBank: needsBank ? vaBank : undefined,
        attribution: attributionForCheckout(),
        meta: pixelCookies(),
        ga: await gaIdsForCheckout(),
        mixpanel: await mixpanelForCheckout(),
      });
      trackOrderPlaced({
        orderNumber: res.orderNumber,
        totalIdr: res.totalIdr,
        paymentMethod: method,
        vaBank: needsBank ? vaBank : undefined,
        courier: courier.courier_code,
        items: trackedItems,
        discountCode: discount?.code,
        pasang: wantsPasang,
      });
      // On to Kanata's own payment page. This line used to be
      // `window.location.href = res.paymentLink` — the end of the Kanata
      // experience, and the reason this project exists. The buyer now stays on
      // kanata.id from cart to confirmation.
      //
      // The token is the buyer's key to their own order: guest checkout is the
      // default path, so there is no session to hang it off. It travels in the
      // URL so a refresh, or the link opened on another tab, still works.
      navigate(`/bayar/${encodeURIComponent(res.orderNumber)}?t=${encodeURIComponent(res.statusToken)}`, {
        // Replace rather than push: the back button should return to the cart,
        // not to a checkout form whose cart has already been consumed.
        replace: true,
      });
    } catch (e) {
      // The order was placed but the chosen method is unavailable — the
      // rollback switch, with no hosted page to fall back to. The order exists
      // and holds its stock, so send them to its payment page to pay another
      // way. Checking out again would place a second order for a second desk.
      const pickVa = qrisUnavailable(e);
      if (pickVa) {
        trackOrderPlaced({
          orderNumber: pickVa.orderNumber,
          totalIdr: total,
          paymentMethod: method,
          courier: courier.courier_code,
          items: trackedItems,
          discountCode: discount?.code,
          pasang: wantsPasang,
          qrisUnavailable: true,
        });
        navigate(
          `/bayar/${encodeURIComponent(pickVa.orderNumber)}?t=${encodeURIComponent(pickVa.statusToken)}`,
          { replace: true },
        );
        return;
      }
      // A quote that no longer holds — expired, or the cart or address changed
      // since it was taken. The server refuses to charge a price that was never
      // true, so drop the stale options and make the customer pick again from
      // fresh ones rather than leaving a dead figure on screen.
      // The code applied when it was entered and no longer does — expired,
      // used up. Take it off the total on screen and say why, rather than
      // leaving a discount the server will not give.
      if (discountRejected(e)) {
        setDiscount(null);
        setDiscountError(e instanceof Error ? e.message : "Kode tidak bisa dipakai.");
      }
      // Switched off in the admin after this page loaded. Untick it and reload
      // the cart, which no longer offers it, so the total on screen is true.
      if (packingKayuRejected(e)) {
        setPacking(false);
        void refresh();
      }
      if (needsRequote(e)) {
        setRates(null);
        setCourier(null);
        setRateError(e instanceof Error ? e.message : "Hitung ulang ongkirnya ya.");
      }
      setError(e instanceof Error ? e.message : "Checkout gagal. Coba lagi sebentar.");
      trackOrderSubmitFailed(e, method, total);
      setSubmitting(false);
    }
  }

  if (!loading && items.length === 0) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-40 text-center sm:px-8">
        <h1 className="text-2xl font-medium text-ink">Keranjang kamu kosong</h1>
        <Button asChild className="mt-6">
          <Link to="/katalog">Lihat semua meja</Link>
        </Button>
      </main>
    );
  }

  return (
    <>
      <Seo title="Checkout — Kanata Indonesia" description="Selesaikan pesanan kamu." noindex />

      <main className="pt-28 sm:pt-36">
        <div className="mx-auto max-w-5xl px-5 pb-20 sm:px-8">
          <h1 className="text-3xl font-medium tracking-tight text-ink sm:text-4xl">Checkout</h1>
          <p className="mt-2 text-ink/60">
            Tidak perlu bikin akun. Isi data pengiriman, pilih kurir, bayar.
          </p>

          <form onSubmit={onSubmit} className="mt-8 grid gap-10 lg:grid-cols-[1fr_20rem] lg:gap-14">
            <div className="space-y-8">
              <section>
                <h2 className="font-medium text-ink">Kontak</h2>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <Field label="Nama lengkap" required>
                    <Input value={form.name} onChange={set("name")} required autoComplete="name" />
                  </Field>
                  <Field label="Nomor HP" required hint="Untuk kurir dan cek status pesanan.">
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      required
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="0815…"
                    />
                  </Field>
                  <Field label="Email" required className="sm:col-span-2">
                    <Input
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      required
                      autoComplete="email"
                    />
                  </Field>
                </div>
              </section>

              <section>
                <h2 className="font-medium text-ink">Alamat pengiriman</h2>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <Field label="Alamat lengkap" required className="sm:col-span-2">
                    <Textarea
                      value={form.addressLine}
                      onChange={set("addressLine")}
                      required
                      placeholder="Nama jalan, nomor rumah, RT/RW, patokan"
                    />
                  </Field>
                  <RegionPicker
                    value={{
                      province: form.province,
                      city: form.city,
                      district: form.district,
                      postalCode: form.postalCode,
                    }}
                    onChange={setRegion}
                  />
                </div>
              </section>

              <section>
                <h2 className="font-medium text-ink">Kurir</h2>
                {quoting && (
                  <p className="mt-2 flex items-center gap-2 text-sm text-ink/55">
                    <Loader2 className="size-4 animate-spin" />
                    Menghitung ongkir ke {form.postalCode}…
                  </p>
                )}
                {!rates && !rateError && !quoting && (
                  <p className="mt-2 text-sm text-ink/55">
                    Pilih alamat sampai kode pos, pilihan kurir dan harganya muncul di sini.
                  </p>
                )}
                {rateError && (
                  <div className="mt-2 flex items-start gap-2 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 size-4 shrink-0" />
                    <span className="flex-1">
                      {rateError}{" "}
                      {rateError.includes("kurir") && (
                        <>
                          Hubungi kami lewat WhatsApp, biasanya masih bisa diatur.
                        </>
                      )}
                    </span>
                    {/* An expired quote or a timeout is fixed by asking again. */}
                    {form.postalCode && !quoting && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => void quote(form.postalCode)}
                      >
                        Hitung ulang
                      </Button>
                    )}
                  </div>
                )}
                {rates && rates.length > 0 && (
                  <RadioGroup
                    className="mt-3 gap-2"
                    // Keyed on the quote id, not the service code: codes like
                    // "reg" and "std" recur across couriers, so two quoted
                    // rates could share one — picking the second selected the
                    // first, and React rendered duplicate keys.
                    value={courier?.quote_id ?? ""}
                    onValueChange={(v) => setCourier(rates.find((r) => r.quote_id === v) ?? null)}
                  >
                    {rates.map((r) => (
                      <RadioCard key={r.quote_id} value={r.quote_id}>
                        <Truck className="size-4 shrink-0 text-ink/40" />
                        <span className="flex-1">
                          <span className="block font-medium text-ink">
                            {r.courier_name} — {r.courier_service_name}
                          </span>
                          <span className="block text-xs text-ink/55">
                            {[r.description, r.duration].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                        <span className="shrink-0 font-medium text-ink">{formatIDR(r.price)}</span>
                      </RadioCard>
                    ))}
                  </RadioGroup>
                )}
              </section>

              {promo?.packingKayu ? (
                <section>
                  <h2 className="font-medium text-ink">Pengemasan</h2>
                  <p className="mt-3 flex items-start gap-3 rounded-xl border border-brand/25 bg-brand-light/40 p-4 text-sm">
                    <PackageCheck className="mt-0.5 size-4 shrink-0 text-brand-dark" />
                    <span className="flex-1">
                      <span className="block font-medium text-ink">Packing kayu, gratis</span>
                      <span className="block text-xs text-ink/55">
                        Termasuk {promo.name}: setiap meja dikemas dalam peti kayu oleh tim gudang Kanata.
                      </span>
                    </span>
                    <span className="shrink-0 text-right tabular-nums">
                      <del className="block text-xs text-ink/45">{formatIDR(promo.packingKayu.totalIdr)}</del>
                      <span className="font-medium text-brand-dark">Rp 0</span>
                    </span>
                  </p>
                </section>
              ) : packingOffer && (
                <section>
                  <h2 className="font-medium text-ink">Pengemasan</h2>
                  <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-ink/10 p-4 transition-colors has-[:checked]:border-ink/30">
                    <Checkbox
                      className="mt-0.5"
                      checked={packing}
                      onCheckedChange={(v) => setPacking(v === true)}
                    />
                    <PackageCheck className="mt-0.5 size-4 shrink-0 text-ink/40" />
                    <span className="flex-1">
                      <span className="block font-medium text-ink">Packing kayu</span>
                      <span className="block text-xs text-ink/55">
                        Meja dikemas dalam peti kayu oleh tim gudang Kanata sebelum dikirim.{" "}
                        {formatIDR(packingOffer.priceIdr)} per meja
                        {packingOffer.units > 1 ? ` × ${packingOffer.units} meja` : ""}.
                      </span>
                    </span>
                    <span className="shrink-0 font-medium text-ink">{formatIDR(packingOffer.totalIdr)}</span>
                  </label>
                </section>
              )}

              <section>
                <h2 className="font-medium text-ink">Pembayaran</h2>
                <RadioGroup
                  className="mt-3 gap-2 sm:grid-cols-2"
                  value={method}
                  onValueChange={(v) => setMethod(v as "QRIS" | "VA")}
                >
                  <RadioCard value="QRIS">
                    <span className="flex-1">
                      <span className="block font-medium text-ink">QRIS</span>
                      <span className="block text-xs text-ink/55">
                        Scan dari aplikasi apa pun, tanpa keluar dari halaman ini.
                      </span>
                    </span>
                  </RadioCard>
                  <RadioCard value="VA">
                    <span className="flex-1">
                      <span className="block font-medium text-ink">Virtual Account</span>
                      <span className="block text-xs text-ink/55">
                        {vaBanks.length > 0
                          ? "Transfer dari m-banking atau ATM ke nomor VA yang muncul setelah ini."
                          : "Transfer dari m-banking. Nomor VA-nya diambil di halaman mitra pembayaran kami."}
                      </span>
                    </span>
                  </RadioCard>
                </RadioGroup>
                {needsBank && (
                  <div className="mt-4">
                    <p id="va-bank-label" className="text-sm font-medium text-ink">
                      Bank untuk virtual account
                    </p>
                    <RadioGroup
                      aria-labelledby="va-bank-label"
                      className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4"
                      value={vaBank}
                      onValueChange={setVaBank}
                    >
                      {vaBanks.map((b) => (
                        <RadioCard key={b.code} value={b.code} className="justify-center text-center">
                          <span className="font-medium text-ink">{b.name}</span>
                        </RadioCard>
                      ))}
                    </RadioGroup>
                  </div>
                )}
              </section>

              <section>
                <Label htmlFor="note">Catatan (opsional)</Label>
                <Textarea
                  id="note"
                  className="mt-2"
                  value={form.note}
                  onChange={set("note")}
                  placeholder="Patokan alamat, waktu pengiriman yang cocok, atau apa pun yang perlu kami tahu."
                />
              </section>
            </div>

            <aside className="lg:sticky lg:top-28 lg:self-start">
              <div className="rounded-2xl border border-ink/8 bg-cream/60 p-6">
                <h2 className="font-medium text-ink">Pesanan kamu</h2>

                <ul className="mt-4 space-y-3">
                  {/* The same rows the cart page shows: an upgrade is one Plus
                      line at the package price, and a line lists only what the
                      upgrade left of it. */}
                  {cartRows(cart).map((row) => {
                    const v = row.kind === "upgrade" ? row.upgrade : row.line;
                    const qty = row.kind === "upgrade" ? row.upgrade.qty : row.qty;
                    const total = row.kind === "upgrade" ? row.upgrade.lineTotalIdr : row.line.lineTotalIdr;
                    return (
                      <li key={`${row.kind}-${v.skuId}`} className="flex justify-between gap-3 text-sm">
                        <span className="min-w-0 text-ink/70">
                          <span className="block truncate">
                            {v.productName}
                            {row.kind === "upgrade" && (
                              <span className="ml-1.5 text-xs text-brand-dark">harga paket</span>
                            )}
                          </span>
                          <span className="text-xs text-ink/45">
                            {[v.size ? `${v.size} cm` : null, v.topColor, v.legColor && `kaki ${v.legColor}`, `×${qty}`]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </span>
                        <span className="shrink-0 text-right tabular-nums text-ink">
                          {row.kind === "line" && row.line.listPriceIdr && qty > 0 ? (
                            <del className="mr-1.5 text-xs text-ink/45">
                              {formatIDR(row.line.listPriceIdr * qty)}
                            </del>
                          ) : null}
                          {formatIDR(total)}
                        </span>
                      </li>
                    );
                  })}
                  {promo?.gift && promo.gift.qty > 0 && (
                    <li className="flex justify-between gap-3 text-sm">
                      <span className="min-w-0 text-ink/70">
                        <span className="block truncate">{promo.gift.productName ?? promo.gift.label}</span>
                        <span className="text-xs text-ink/45">
                          ×{promo.gift.qty} · <span className="text-brand-dark">{promo.name}</span>
                        </span>
                      </span>
                      <span className="shrink-0 text-right tabular-nums">
                        <del className="mr-1.5 text-xs text-ink/45">{formatIDR(totals.promoGiftIdr)}</del>
                        <span className="text-brand-dark">Rp 0</span>
                      </span>
                    </li>
                  )}
                  {promo?.packingKayu && (
                    <li className="flex justify-between gap-3 text-sm">
                      <span className="min-w-0 text-ink/70">
                        <span className="block truncate">Packing kayu</span>
                        <span className="text-xs text-ink/45">
                          {promo.packingKayu.units} meja · <span className="text-brand-dark">{promo.name}</span>
                        </span>
                      </span>
                      <span className="shrink-0 text-right tabular-nums">
                        <del className="mr-1.5 text-xs text-ink/45">{formatIDR(promo.packingKayu.totalIdr)}</del>
                        <span className="text-brand-dark">Rp 0</span>
                      </span>
                    </li>
                  )}
                </ul>

                <Separator className="my-4" />

                <div className="mb-4">
                  {discount ? (
                    <div className="flex items-center justify-between rounded-xl bg-brand-light/60 px-3 py-2 text-sm text-brand-dark">
                      <span>
                        Kode <span className="font-medium">{discount.code}</span> dipakai
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setDiscount(null);
                          setCodeInput("");
                        }}
                        className="text-xs underline underline-offset-2"
                      >
                        Hapus
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        aria-label="Kode diskon"
                        placeholder="Kode diskon"
                        value={codeInput}
                        onChange={(e) => setCodeInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void onApplyCode();
                          }
                        }}
                        className="h-10"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={applying || !codeInput.trim()}
                        onClick={() => void onApplyCode()}
                        className="h-10 shrink-0"
                      >
                        {applying ? <Loader2 className="size-4 animate-spin" /> : "Pakai"}
                      </Button>
                    </div>
                  )}
                  {discountError && (
                    <p role="alert" className="mt-2 text-xs text-destructive">
                      {discountError}
                    </p>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  {/* Already off the subtotal below, as on the cart page: said
                      here so the struck-through prices above add up to
                      something. */}
                  {(cart?.listSavingIdr ?? 0) > 0 && (
                    <Row label="Hemat dari harga normal" value={`−${formatIDR(cart?.listSavingIdr ?? 0)}`} muted />
                  )}
                  <Row label="Subtotal" value={formatIDR(subtotal)} />
                  {discount && discountIdr > 0 && (
                    <Row label={`Diskon (${discount.code})`} value={`−${formatIDR(discountIdr)}`} />
                  )}
                  {/* Waived packing kayu is a free line above, like the gift,
                      rather than a charge and its own refund down here. */}
                  {packingIdr > 0 && totals.promoPackingIdr === 0 && (
                    <Row label="Packing kayu" value={formatIDR(packingIdr)} />
                  )}
                  <Row
                    label="Ongkos kirim"
                    value={courier ? formatIDR(shipping) : "Belum dihitung"}
                    muted={!courier}
                  />
                  {promo && promo.shippingSubsidyMaxIdr > 0 && (
                    <Row
                      label="Subsidi ongkir"
                      value={
                        courier
                          ? `−${formatIDR(totals.shippingSubsidyIdr)}`
                          : `s/d ${formatIDR(promo.shippingSubsidyMaxIdr)}`
                      }
                      muted={!courier}
                    />
                  )}
                  {wantsPasang && (
                    <Row label={pasangCopy.label} value={pasangCopy.checkoutValue} muted />
                  )}
                </div>

                <Separator className="my-4" />

                <div className="flex items-baseline justify-between">
                  <span className="font-medium text-ink">Total</span>
                  <span className="text-xl font-medium text-ink">{formatIDR(total)}</span>
                </div>

                {error && (
                  <p className="mt-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="mt-5 w-full"
                  disabled={!courier || submitting || items.length === 0 || (needsBank && !vaBank)}
                >
                  {submitting && <Loader2 className="animate-spin" />}
                  {submitting ? "Menyiapkan pembayaran…" : "Bayar sekarang"}
                </Button>

                {!courier && (
                  <p className="mt-3 text-center text-xs text-ink/50">
                    Pilih kurir dulu untuk melanjutkan.
                  </p>
                )}
              </div>
            </aside>
          </form>
        </div>
      </main>
      {pasangNotice.dialog}
    </>
  );
}

function Field({
  label,
  required,
  hint,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <Label className="mb-1.5 block">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
      {hint && <p className="mt-1 text-xs text-ink/50">{hint}</p>}
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink/60">{label}</span>
      <span className={`shrink-0 whitespace-nowrap ${muted ? "text-ink/50" : "font-medium text-ink"}`}>{value}</span>
    </div>
  );
}
